import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyRow, PageHeader, Spinner, TableWrap, Th } from '../../components/ui';
import { AdminOnly, useBuildingMe } from './Guard';
import { BASE, INVITE_LINK, InviteAnswer, PhoneArray, StaffArray, keys, useBuildingZone } from './api';
import type { SrMarshalPhone, SrStaff } from './api';

/** Marshal phones: invite a staff member's phone to the Companion app, see enrolled phones, remove one. Administrators only. */
export function PhonesPage() {
  return (
    <AdminOnly>
      <PhonesInner />
    </AdminOnly>
  );
}

function PhonesInner() {
  const qc = useQueryClient();
  const toast = useToast();
  const zone = useBuildingZone();
  const me = useBuildingMe();
  const staff = useQuery({ queryKey: keys.staff, queryFn: () => api.get(`${BASE}/staff`, { schema: StaffArray }) });
  const phones = useQuery({ queryKey: keys.phones, queryFn: () => api.get(`${BASE}/phones`, { schema: PhoneArray }) });
  const [invited, setInvited] = useState<{ name: string; answer: InviteAnswer } | null>(null);
  const invite = useMutation({
    mutationFn: (person: SrStaff) =>
      api.post(`${BASE}/phones/invitations`, { body: { accountId: person.accountId }, schema: InviteAnswer }).then((answer) => ({ name: person.fullName, answer })),
    onSuccess: (r) => setInvited(r),
  });
  const remove = useMutation({
    mutationFn: (phoneId: string) => api.delete(`${BASE}/phones/${phoneId}`),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: keys.phones }), qc.invalidateQueries({ queryKey: keys.staff })]);
      toast(t('srb.phones.removed'));
    },
  });
  const off = me.data ? !me.data.building.settings.marshalPhones.enabled : false;
  return (
    <>
      <PageHeader title={t('srb.phones.title')} description={t('srb.phones.desc')} />
      <div className="space-y-6">
        {off ? <Alert kind="info">{t('srb.phones.off')}</Alert> : null}
        {invited ? <InviteResult name={invited.name} answer={invited.answer} zone={zone} onClose={() => setInvited(null)} /> : null}
        <Card title={t('srb.phones.staff')} description={t('srb.phones.staffDesc')}>
          <ErrorLine error={invite.error} />
          {staff.isPending ? (
            <Spinner />
          ) : staff.isError ? (
            <ErrorPanel error={staff.error} onRetry={() => void staff.refetch()} />
          ) : (
            <TableWrap caption={t('srb.phones.staff')}>
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <Th>{t('srb.phones.col.person')}</Th>
                  <Th>{t('srb.phones.col.role')}</Th>
                  <Th>{t('srb.phones.col.phones')}</Th>
                  <Th>{t('srb.phones.col.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {staff.data.length === 0 ? <EmptyRow cols={4} /> : null}
                {staff.data.map((s) => (
                  <tr key={s.accountId} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <span className="font-medium">{s.fullName}</span>
                      <span className="block text-xs text-slate-700">{s.email}</span>
                    </td>
                    <td className="px-4 py-3">{t(`sr.role.${s.role}`)}</td>
                    <td className="px-4 py-3">{s.phones}</td>
                    <td className="px-4 py-3">
                      <Button disabled={off || invite.isPending} onClick={() => invite.mutate(s)} aria-label={t('srb.phones.inviteNamed', { name: s.fullName })}>
                        {t('srb.phones.invite')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>

        <Card title={t('srb.phones.enrolled')} description={t('srb.phones.enrolledDesc')}>
          <ErrorLine error={remove.error} />
          {phones.isPending ? (
            <Spinner />
          ) : phones.isError ? (
            <ErrorPanel error={phones.error} onRetry={() => void phones.refetch()} />
          ) : (
            <TableWrap caption={t('srb.phones.enrolled')}>
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <Th>{t('srb.phones.col.person')}</Th>
                  <Th>{t('srb.phones.col.phone')}</Th>
                  <Th>{t('srb.phones.col.enrolled')}</Th>
                  <Th>{t('srb.phones.col.seen')}</Th>
                  <Th>{t('srb.phones.col.expires')}</Th>
                  <Th>{t('srb.phones.col.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {phones.data.length === 0 ? <EmptyRow cols={6} /> : null}
                {phones.data.map((p: SrMarshalPhone) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium">{p.fullName}</td>
                    <td className="px-4 py-3">{[p.model, p.platform, p.appVersion].filter(Boolean).join(' ')}</td>
                    <td className="px-4 py-3">{formatDateTime(p.enrolledAt, zone)}</td>
                    <td className="px-4 py-3">{p.lastSeenAt ? formatDateTime(p.lastSeenAt, zone) : t('srb.phones.never')}</td>
                    <td className="px-4 py-3">{p.revokedAt ? <Badge>{t('srb.phones.removedBadge')}</Badge> : p.expiresAt ? formatDateTime(p.expiresAt, zone) : t('srb.phones.noExpiry')}</td>
                    <td className="px-4 py-3">
                      {!p.revokedAt ? (
                        <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(p.id)} aria-label={t('srb.phones.removeNamed', { name: p.fullName })}>
                          {t('srb.phones.remove')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      </div>
    </>
  );
}

/** What happened after "Invite": the email notice, and (when the server returns it) the one-off link and its QR code. */
function InviteResult({ name, answer, zone, onClose }: { name: string; answer: InviteAnswer; zone: string; onClose: () => void }) {
  const link = answer.link && INVITE_LINK.test(answer.link) ? answer.link : null;
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!link) return;
    let cancelled = false;
    QRCode.toDataURL(link, { margin: 1, width: 224 })
      .then((d) => {
        if (!cancelled) setQr(d);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [link]);
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div role="status">
      <Alert kind="success">
        <p className="font-medium">{t('srb.invite.sent', { name, email: answer.emailMasked })}</p>
        <p className="mt-1">{t('srb.invite.expires', { time: formatDateTime(answer.expiresAt, zone) })}</p>
        <p className="mt-1">{t('srb.invite.onceOnly')}</p>
        {link ? (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
            {qr ? <img src={qr} width={224} height={224} alt={t('srb.invite.qrAlt', { name })} className="rounded bg-white" /> : null}
            <div className="min-w-0">
              <p className="text-xs">{t('srb.invite.linkLabel')}</p>
              <p className="break-all font-mono text-xs" data-testid="invite-link">
                {link}
              </p>
              <Button className="mt-2" onClick={() => void copy()}>
                {copied ? t('srb.invite.copied') : t('srb.invite.copy')}
              </Button>
            </div>
          </div>
        ) : null}
        <Button className="mt-3" onClick={onClose}>
          {t('srb.invite.done')}
        </Button>
      </Alert>
    </div>
  );
}
